/**
 * Structural Beam Tool - BIM 2025 Grade
 * 
 * Horizontal structural framing elements with full complexity.
 * Features:
 * - Line-based placement with snapping
 * - Start/End cutback for connections
 * - Camber specification
 * - Cross-section rotation
 * - Sloped beams
 * - Multiple structural usages
 * - Moment/Shear connection types
 * - Coping geometry
 * - Haunched/Tapered beams
 * - Reference level constraints
 */

import { v4 as uuidv4 } from 'uuid';
import { Element, StructuralMaterial, AnalyticalModel, RebarHostData, Constraint } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

// ==================== Enums and Types ====================

export type BeamStructuralUsage =
    | 'Girder'      // Primary beam supporting other beams
    | 'Joist'       // Secondary beam supporting floor
    | 'Purlin'      // Roof beam
    | 'Girt'        // Wall horizontal member
    | 'Horizontal Bracing'
    | 'Other';

export type BeamJustification =
    | 'Top'         // Top of beam at reference line
    | 'Center'      // Center at reference line
    | 'Bottom'      // Bottom at reference line
    | 'Origin';     // Family origin at reference line

export type ConnectionType =
    | 'Moment Frame'      // Full moment connection
    | 'Shear'             // Pinned/shear only
    | 'Cantilever'        // Free end
    | 'Auto';             // Determined by connections

export type CamberType =
    | 'None'
    | 'Arc'           // Circular arc camber
    | 'Parabolic';    // Parabolic camber

export type CopeType =
    | 'None'
    | 'Top'           // Top flange coped
    | 'Bottom'        // Bottom flange coped
    | 'Both';         // Both flanges coped

// ==================== Beam Profile ====================

export interface BeamProfile {
    shape: 'Rectangular' | 'I-Shape' | 'C-Shape' | 'L-Shape' | 'T-Shape' | 'Circular' | 'Hollow';

    // Common dimensions (mm)
    width: number;      // Overall width (B)
    depth: number;      // Overall depth (H)

    // I-Shape specific
    flangeWidth?: number;       // bf
    flangeThickness?: number;   // tf
    webThickness?: number;      // tw
    filletRadius?: number;      // r

    // Hollow/Tube specific
    wallThickness?: number;
    cornerRadius?: number;

    // Circular
    diameter?: number;
}

// ==================== Cutback/Coping ====================

export interface BeamCutback {
    startCutback: number;   // mm - Distance to cut from start
    endCutback: number;     // mm - Distance to cut from end
}

export interface BeamCope {
    startType: CopeType;
    endType: CopeType;

    startTopDepth?: number;     // mm
    startTopLength?: number;    // mm
    startBottomDepth?: number;
    startBottomLength?: number;

    endTopDepth?: number;
    endTopLength?: number;
    endBottomDepth?: number;
    endBottomLength?: number;
}

// ==================== Camber ====================

export interface BeamCamber {
    type: CamberType;
    value: number;      // mm - Maximum camber at midspan
    length?: number;    // mm - Camber length (if partial)
}

// ==================== Haunched Beam ====================

export interface HaunchData {
    enabled: boolean;
    startDepth?: number;    // mm - Additional depth at start
    startLength?: number;   // mm - Length of haunch at start
    endDepth?: number;      // mm - Additional depth at end
    endLength?: number;     // mm - Length of haunch at end
    profile?: 'Linear' | 'Parabolic';
}

// ==================== Enhanced Geometry Data ====================

export interface EnhancedBeamGeometryData {
    // Core geometry
    start: [number, number, number];
    end: [number, number, number];

    // Profile
    profile: BeamProfile;
    rotation: number;       // Cross-section rotation (degrees)
    justification: BeamJustification;

    // Level constraints
    referenceLevelId: string;
    startLevelOffset: number;   // mm
    endLevelOffset: number;     // mm

    // Connection modifications
    cutback: BeamCutback;
    cope: BeamCope;

    // Camber
    camber: BeamCamber;

    // Haunching
    haunch: HaunchData;

    // Calculated values
    actualLength: number;   // m - Total centerline length
    cutLength: number;      // m - Length after cutbacks
}

// ==================== Enhanced Beam Element ====================

export interface EnhancedBeamElement extends Element {
    category: 'Structural Framing';
    geometryData: EnhancedBeamGeometryData;

    // Connection information
    startConnection?: {
        type: ConnectionType;
        connectedTo?: string;   // Element ID
        plateType?: string;
        boltPattern?: string;
    };
    endConnection?: {
        type: ConnectionType;
        connectedTo?: string;
        plateType?: string;
        boltPattern?: string;
    };

    // Structural data
    structuralMaterial: StructuralMaterial;
    analyticalModel: AnalyticalModel;
    rebarHostData: RebarHostData;

    // Fabrication data
    fabrication?: {
        mark: string;
        pieceNumber: number;
        shopDrawingRef?: string;
        weight?: number;        // kg
        surfaceArea?: number;   // m²
    };
}

// ==================== Tool Options ====================

export interface EnhancedBeamToolOptions {
    // Type selection
    familyName: string;
    typeName: string;

    // Profile (from type or override)
    profile: BeamProfile;

    // Placement
    referenceLevelId: string;
    startLevelOffset: number;
    endLevelOffset: number;
    justification: BeamJustification;
    rotation: number;

    // Structural
    structuralUsage: BeamStructuralUsage;
    materialId: string;

    // Connection settings
    startConnectionType: ConnectionType;
    endConnectionType: ConnectionType;

    // Cutback defaults
    startCutback: number;
    endCutback: number;

    // Camber
    camber: BeamCamber;

    // Chain placement
    chainMode: boolean;
}

// Standard beam profiles from families
export const STANDARD_BEAM_PROFILES: Record<string, BeamProfile> = {
    'W-Wide Flange: W530x92': {
        shape: 'I-Shape',
        width: 209,
        depth: 533,
        flangeWidth: 209,
        flangeThickness: 15.6,
        webThickness: 10.2,
        filletRadius: 12
    },
    'W-Wide Flange: W410x60': {
        shape: 'I-Shape',
        width: 178,
        depth: 407,
        flangeWidth: 178,
        flangeThickness: 12.8,
        webThickness: 7.7,
        filletRadius: 10
    },
    'M_Concrete-Rectangular Beam: 300 x 600': {
        shape: 'Rectangular',
        width: 300,
        depth: 600
    },
    'M_Concrete-Rectangular Beam: 400 x 800': {
        shape: 'Rectangular',
        width: 400,
        depth: 800
    },
    'HSS-Hollow Structural Section: HSS203.2x101.6x9.5': {
        shape: 'Hollow',
        width: 203.2,
        depth: 101.6,
        wallThickness: 9.5,
        cornerRadius: 19
    },
    'Circular-Steel: 200mm': {
        shape: 'Circular',
        width: 200,
        depth: 200,
        diameter: 200
    },
    'LVL-Laminated Veneer Lumber: 45 x 300mm': {
        shape: 'Rectangular',
        width: 45,
        depth: 300
    }
};

// ==================== Beam Tool Class ====================

export class BeamTool extends BaseTool {
    id = 'beam';
    name = 'Beam';
    cursor = 'crosshair';

    private startPoint: THREE.Vector3 | null = null;
    private currentPoint: THREE.Vector3 | null = null;
    private placedBeamIds: string[] = [];  // For chain mode

    // Current options (would be set from UI)
    private options: EnhancedBeamToolOptions = this.getDefaultOptions();

    private getDefaultOptions(): EnhancedBeamToolOptions {
        return {
            familyName: 'M_Concrete-Rectangular Beam',
            typeName: '300 x 600',
            profile: STANDARD_BEAM_PROFILES['M_Concrete-Rectangular Beam: 300 x 600'],
            referenceLevelId: 'Level 1',
            startLevelOffset: 0,
            endLevelOffset: 0,
            justification: 'Center',
            rotation: 0,
            structuralUsage: 'Girder',
            materialId: 'mat-concrete-30',
            startConnectionType: 'Auto',
            endConnectionType: 'Auto',
            startCutback: 0,
            endCutback: 0,
            camber: { type: 'None', value: 0 },
            chainMode: true
        };
    }

    /**
     * Set beam options from UI
     */
    setOptions(options: Partial<EnhancedBeamToolOptions>): void {
        this.options = { ...this.options, ...options };
    }

    /**
     * Get current options
     */
    getOptions(): EnhancedBeamToolOptions {
        return { ...this.options };
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;

        // Apply grid snapping
        let snapped = this.snapToGrid(point);

        // Check for structural connection snapping
        snapped = this.snapToStructuralConnection(snapped) || snapped;

        if (!this.startPoint) {
            // First click - set start point
            this.startPoint = snapped;
            this.currentPoint = snapped;
        } else {
            // Second click - create beam
            if (snapped.distanceTo(this.startPoint) > 0.01) {
                const beam = this.createBeam(this.startPoint, snapped);
                this.context.bimStore.addElement(beam);
                this.placedBeamIds.push(beam.id);

                // Auto-detect connections
                this.detectAndSetConnections(beam);

                if (this.options.chainMode) {
                    // Chain mode - start next beam from end
                    this.startPoint = snapped;
                    this.currentPoint = snapped;
                } else {
                    // Single placement mode - reset
                    this.startPoint = null;
                    this.currentPoint = null;
                }
            }
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;

        const point = this.getIntersection(event);
        if (point) {
            let snapped = this.snapToGrid(point);
            snapped = this.snapToStructuralConnection(snapped) || snapped;
            this.currentPoint = snapped;
            this.updatePreview();
        }
    }

    onPointerUp(): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            if (this.startPoint) {
                // Cancel current placement
                this.startPoint = null;
                this.currentPoint = null;
                this.placedBeamIds = [];
                if (this.context?.appStore) {
                    this.context.appStore.setToolPreview(null);
                }
            } else {
                super.onKeyDown(event);
            }
        } else if (event.key === 'Enter') {
            // Finish chain
            this.startPoint = null;
            this.currentPoint = null;
            this.placedBeamIds = [];
            if (this.context?.appStore) {
                this.context.appStore.setToolPreview(null);
            }
        }
    }

    private snapToStructuralConnection(point: THREE.Vector3): THREE.Vector3 | null {
        if (!this.context) return null;

        const tolerance = 0.3; // 300mm snap radius
        let closest: THREE.Vector3 | null = null;
        let closestDist = tolerance;

        // Find nearby beam/column endpoints
        this.context.bimStore.project.elements.forEach(element => {
            if (element.category === 'Structural Framing' || element.category === 'Structural Columns') {
                const geom = element.geometryData as any;
                if (geom?.start && geom?.end) {
                    const start = new THREE.Vector3(...geom.start);
                    const end = new THREE.Vector3(...geom.end);

                    const distStart = point.distanceTo(start);
                    const distEnd = point.distanceTo(end);

                    if (distStart < closestDist) {
                        closestDist = distStart;
                        closest = start.clone();
                    }
                    if (distEnd < closestDist) {
                        closestDist = distEnd;
                        closest = end.clone();
                    }
                }
            }
        });

        return closest;
    }

    private updatePreview(): void {
        if (!this.context) return;

        if (this.startPoint && this.currentPoint) {
            const profile = this.options.profile;

            this.context.appStore.setToolPreview({
                type: 'beam',
                start: this.startPoint,
                end: this.currentPoint,
                width: profile.width / 1000,
                depth: profile.depth / 1000,
                rotation: this.options.rotation,
                justification: this.options.justification,
                profile: profile.shape
            });
        } else {
            this.context.appStore.setToolPreview(null);
        }
    }

    private createBeam(start: THREE.Vector3, end: THREE.Vector3): EnhancedBeamElement {
        const length = start.distanceTo(end);
        const cutLength = length - (this.options.startCutback + this.options.endCutback) / 1000;

        const geometryData: EnhancedBeamGeometryData = {
            start: [start.x, start.y, start.z],
            end: [end.x, end.y, end.z],
            profile: { ...this.options.profile },
            rotation: this.options.rotation,
            justification: this.options.justification,
            referenceLevelId: this.options.referenceLevelId,
            startLevelOffset: this.options.startLevelOffset,
            endLevelOffset: this.options.endLevelOffset,
            cutback: {
                startCutback: this.options.startCutback,
                endCutback: this.options.endCutback
            },
            cope: {
                startType: 'None',
                endType: 'None'
            },
            camber: { ...this.options.camber },
            haunch: { enabled: false },
            actualLength: length,
            cutLength: cutLength
        };

        // Create analytical model
        const analyticalModel = this.createAnalyticalModel(start, end);

        // Get material
        const structuralMaterial = this.getMaterial(this.options.materialId);

        // Rebar hosting
        const rebarHostData = this.getRebarHostData();

        // Calculate weight
        const weight = this.calculateWeight(geometryData, structuralMaterial);

        const beam: EnhancedBeamElement = {
            id: uuidv4(),
            category: 'Structural Framing',
            type: `${this.options.familyName}: ${this.options.typeName}`,
            parameters: this.buildParameters(geometryData, weight),
            constraints: this.buildConstraints(),
            geometryData,
            startConnection: {
                type: this.options.startConnectionType
            },
            endConnection: {
                type: this.options.endConnectionType
            },
            structuralMaterial,
            analyticalModel,
            rebarHostData,
            fabrication: {
                mark: '',
                pieceNumber: 0,
                weight
            }
        };

        return beam;
    }

    private createAnalyticalModel(start: THREE.Vector3, end: THREE.Vector3): AnalyticalModel {
        const startNode = {
            id: uuidv4(),
            position: [start.x, start.y, start.z] as [number, number, number],
            boundaryConditions: this.getBoundaryCondition(this.options.startConnectionType)
        };

        const endNode = {
            id: uuidv4(),
            position: [end.x, end.y, end.z] as [number, number, number],
            boundaryConditions: this.getBoundaryCondition(this.options.endConnectionType)
        };

        return {
            nodes: [startNode, endNode],
            curves: [{
                startNodeId: startNode.id,
                endNodeId: endNode.id,
                type: 'Beam' as const
            }]
        };
    }

    private getBoundaryCondition(connectionType: ConnectionType): 'Fixed' | 'Pinned' | 'Roller' | 'Free' {
        switch (connectionType) {
            case 'Moment Frame': return 'Fixed';
            case 'Shear': return 'Pinned';
            case 'Cantilever': return 'Free';
            default: return 'Pinned';
        }
    }

    private getMaterial(materialId: string): StructuralMaterial {
        // Material library - would be from FamilyManager in production
        const materials: Record<string, StructuralMaterial> = {
            'mat-concrete-30': {
                id: 'mat-concrete-30',
                name: 'Concrete - C30/37',
                type: 'Concrete',
                properties: {
                    youngsModulus: 30000,
                    shearModulus: 12500,
                    poissonRatio: 0.2,
                    density: 2400,
                    thermalExpansionCoefficient: 0.00001,
                    concreteCompression: 30
                }
            },
            'mat-steel-350': {
                id: 'mat-steel-350',
                name: 'Steel - S350',
                type: 'Steel',
                properties: {
                    youngsModulus: 210000,
                    shearModulus: 81000,
                    poissonRatio: 0.3,
                    density: 7850,
                    thermalExpansionCoefficient: 0.000012,
                    yieldStrength: 350
                }
            },
            'mat-wood-gl24h': {
                id: 'mat-wood-gl24h',
                name: 'Glulam - GL24h',
                type: 'Wood',
                properties: {
                    youngsModulus: 11500,
                    shearModulus: 720,
                    poissonRatio: 0.35,
                    density: 380,
                    thermalExpansionCoefficient: 0.000005
                }
            }
        };

        return materials[materialId] || materials['mat-concrete-30'];
    }

    private getRebarHostData(): RebarHostData {
        // const profile = this.options.profile;
        const isConcrete = this.options.materialId.includes('concrete');

        return {
            validHost: isConcrete,
            coverSettings: {
                top: isConcrete ? 40 : 0,
                bottom: isConcrete ? 40 : 0,
                sides: isConcrete ? 40 : 0
            }
        };
    }

    private calculateWeight(geom: EnhancedBeamGeometryData, material: StructuralMaterial): number {
        const profile = geom.profile;
        let area = 0; // m²

        switch (profile.shape) {
            case 'Rectangular':
                area = (profile.width / 1000) * (profile.depth / 1000);
                break;
            case 'I-Shape': {
                // Approximate I-shape area
                const flangeArea = 2 * (profile.flangeWidth! / 1000) * (profile.flangeThickness! / 1000);
                const webArea = ((profile.depth! - 2 * profile.flangeThickness!) / 1000) * (profile.webThickness! / 1000);
                area = flangeArea + webArea;
                break;
            }
            case 'Hollow': {
                const outer = (profile.width / 1000) * (profile.depth / 1000);
                const inner = ((profile.width - 2 * profile.wallThickness!) / 1000) *
                    ((profile.depth - 2 * profile.wallThickness!) / 1000);
                area = outer - inner;
                break;
            }
            case 'Circular':
                area = Math.PI * Math.pow((profile.diameter! / 2000), 2);
                break;
            default:
                area = (profile.width / 1000) * (profile.depth / 1000);
        }

        const volume = area * geom.cutLength;
        return volume * material.properties.density;
    }

    private buildParameters(geom: EnhancedBeamGeometryData, weight: number): Record<string, any> {
        return {
            // Dimensions
            'Length': geom.actualLength,
            'Cut Length': geom.cutLength,
            'b': geom.profile.width,
            'h': geom.profile.depth,

            // Placement
            'Reference Level': geom.referenceLevelId,
            'Start Level Offset': geom.startLevelOffset,
            'End Level Offset': geom.endLevelOffset,
            'Cross-Section Rotation': geom.rotation,
            'z Justification': geom.justification,

            // Cutback
            'Start Extension': -geom.cutback.startCutback,
            'End Extension': -geom.cutback.endCutback,

            // Camber
            'Camber': geom.camber.value,
            'Camber Type': geom.camber.type,

            // Structural
            'Structural Usage': this.options.structuralUsage,
            'Structural Material': this.options.materialId,

            // Fabrication
            'Weight': weight,
            'Volume': geom.cutLength * (geom.profile.width / 1000) * (geom.profile.depth / 1000)
        };
    }

    protected buildConstraints(): Constraint[] {
        const constraints: Constraint[] = [];

        // Level constraint
        constraints.push({
            type: 'LEVEL_BASED',
            targetId: this.options.referenceLevelId,
            data: {
                offsetType: 'StartAndEnd',
                startOffset: this.options.startLevelOffset,
                endOffset: this.options.endLevelOffset
            }
        });

        return constraints;
    }

    private detectAndSetConnections(beam: EnhancedBeamElement): void {
        if (!this.context) return;

        const tolerance = 0.05; // 50mm
        const start = new THREE.Vector3(...beam.geometryData.start);
        const end = new THREE.Vector3(...beam.geometryData.end);

        this.context.bimStore.project.elements.forEach((element, id) => {
            if (id === beam.id) return;
            if (element.category !== 'Structural Framing' && element.category !== 'Structural Columns') return;

            const geom = element.geometryData as any;
            if (!geom?.start || !geom?.end) return;

            const elemStart = new THREE.Vector3(...geom.start);
            const elemEnd = new THREE.Vector3(...geom.end);

            // Check start connection
            if (start.distanceTo(elemStart) < tolerance || start.distanceTo(elemEnd) < tolerance) {
                beam.startConnection = {
                    type: element.category === 'Structural Columns' ? 'Moment Frame' : 'Shear',
                    connectedTo: id
                };
            }

            // Check end connection
            if (end.distanceTo(elemStart) < tolerance || end.distanceTo(elemEnd) < tolerance) {
                beam.endConnection = {
                    type: element.category === 'Structural Columns' ? 'Moment Frame' : 'Shear',
                    connectedTo: id
                };
            }
        });
    }
}

// ==================== Helper Functions ====================

/**
 * Create a beam element with options (for external use)
 */
export function createEnhancedBeam(
    start: THREE.Vector3,
    end: THREE.Vector3,
    options: Partial<EnhancedBeamToolOptions>
): EnhancedBeamElement {
    const tool = new BeamTool();
    tool.setOptions(options);
    return (tool as any).createBeam(start, end);
}

/**
 * Calculate camber points along beam
 */
export function calculateCamberPoints(
    start: THREE.Vector3,
    end: THREE.Vector3,
    camber: BeamCamber,
    segments: number = 10
): THREE.Vector3[] {
    const points: THREE.Vector3[] = [];
    const dir = end.clone().sub(start);
    const length = dir.length();
    dir.normalize();

    // Up vector (perpendicular to beam in gravity direction)
    const up = new THREE.Vector3(0, 0, 1);

    for (let i = 0; i <= segments; i++) {
        const t = i / segments;
        const point = start.clone().add(dir.clone().multiplyScalar(length * t));

        if (camber.type !== 'None' && camber.value > 0) {
            let offset = 0;
            if (camber.type === 'Arc') {
                // Circular arc: y = sqrt(R² - x²) - offset
                offset = camber.value * Math.sin(Math.PI * t) / 1000;
            } else if (camber.type === 'Parabolic') {
                // Parabola: y = 4h * t * (1-t)
                offset = 4 * (camber.value / 1000) * t * (1 - t);
            }
            point.add(up.clone().multiplyScalar(offset));
        }

        points.push(point);
    }

    return points;
}

/**
 * Get available beam types
 */
export function getAvailableBeamTypes(): { family: string; type: string; profile: BeamProfile }[] {
    return Object.entries(STANDARD_BEAM_PROFILES).map(([key, profile]) => {
        const [family, type] = key.split(': ');
        return { family, type, profile };
    });
}

// Export types for use in other modules
